import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  FacebookAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  User
} from 'firebase/auth';
import { auth } from './firebase';

// Email/Password Sign Up
export const signUp = async (email: string, pass: string) => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) throw new Error("Firebase não configurado");
    const userCredential = await createUserWithEmailAndPassword(auth, email, pass);
    return userCredential.user;
  } catch (error: any) {
    throw error;
  }
};

// Email/Password Sign In
export const signIn = async (email: string, pass: string) => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) throw new Error("Firebase não configurado");
    const userCredential = await signInWithEmailAndPassword(auth, email, pass);
    return userCredential.user;
  } catch (error: any) {
    throw error;
  }
};

// Sign Out
export const logOut = async () => {
  try {
    if (!auth) return;
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Password Reset
export const resetPassword = async (email: string) => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) throw new Error("Firebase não configurado");
    await sendPasswordResetEmail(auth, email);
    return true;
  } catch (error: any) {
    throw error;
  }
};

const shouldUseRedirectAuth = () => {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // Redirect apenas em ambientes embutidos (WebView/in-app browsers),
  // onde popup tende a falhar ou ser bloqueado.
  const isEmbeddedBrowser = /wv|WebView|Instagram|FBAN|FBAV|Line\//i.test(ua);
  return isEmbeddedBrowser;
};

// Google Sign In
export const signInWithGoogle = async () => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) throw new Error("Firebase não configurado");
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    if (shouldUseRedirectAuth()) {
      await signInWithRedirect(auth, provider);
      return null;
    }

    try {
      const result = await signInWithPopup(auth, provider);
      return result.user;
    } catch (popupError: any) {
      const code = popupError?.code || '';
      if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
        await signInWithRedirect(auth, provider);
        return null;
      }
      throw popupError;
    }
  } catch (error: any) {
    throw error;
  }
};

// Facebook Sign In
export const signInWithFacebook = async () => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) throw new Error("Firebase não configurado");
    const provider = new FacebookAuthProvider();
    provider.addScope('email');

    if (shouldUseRedirectAuth()) {
      await signInWithRedirect(auth, provider);
      return null;
    }

    const result = await signInWithPopup(auth, provider);
    return result.user;
  } catch (error: any) {
    throw error;
  }
};

export const handleOAuthRedirectResult = async () => {
  try {
    if (!import.meta.env.VITE_FIREBASE_API_KEY) return null;
    const result = await getRedirectResult(auth);
    return result?.user || null;
  } catch {
    return null;
  }
};

// Subscribe to Auth Changes
export const subscribeToAuthChanges = (callback: (user: User | null) => void) => {
  if (!auth || !import.meta.env.VITE_FIREBASE_API_KEY) {
    callback(null);
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};
